<template>
  <el-input-number
    ref="inputNumberRef"
    v-bind="inputNumberBindings"
    :model-value="localValue"
    @input.capture="syncInputValidity"
    @compositionstart.capture="handleCompositionStart"
    @compositionupdate.capture="handleCompositionUpdate"
    @compositionend.capture="handleCompositionEnd"
    @update:model-value="handleModelUpdate"
    @input="handleInput"
    @change="handleChange"
    @focus="handleFocus"
    @blur="handleBlur"
  >
    <template
      v-for="(_, name) in $slots"
      #[name]="slotProps"
    >
      <slot
        :name="name"
        v-bind="slotProps || {}"
      />
    </template>
  </el-input-number>
</template>

<script setup lang="ts">
import { inputNumberProps, type InputNumberInstance } from 'element-plus';

/** 二次封装 Element Plus 数字输入框，隔离 IME 非数字中间态并在失焦时清空非法值。 */
defineOptions({
  name: 'NumberInput',
  inheritAttrs: false,
});

const props = defineProps(inputNumberProps);
type NumberValue = number | null | undefined;

const emit = defineEmits<{
  (event: 'update:modelValue', value: NumberValue): void;
  (event: 'input', value: NumberValue): void;
  (event: 'change', currentValue: NumberValue, previousValue: NumberValue): void;
  (event: 'focus', focusEvent: FocusEvent): void;
  (event: 'blur', focusEvent: FocusEvent): void;
}>();

const attrs = useAttrs();
const inputNumberBindings = computed(() => ({ ...props, ...attrs }));
const inputNumberRef = ref<InputNumberInstance>();
const localValue = ref<NumberValue>(props.modelValue);
const isComposing = ref(false);
const hasInvalidInput = ref(false);

watch(
  () => props.modelValue,
  (value) => {
    localValue.value = typeof value === 'number' && !Number.isFinite(value) ? null : value;
  },
);

/** 判断输入框当前文本是否为空或可转换为有限数字。 */
const isValidNumericText = (value: string) => {
  const normalizedValue = value.trim();
  if (!normalizedValue) return true;
  if (props.disabledScientific && /[eE]/.test(normalizedValue)) return false;
  return Number.isFinite(Number(normalizedValue));
};

const getInput = (event: Event) => (event.target instanceof HTMLInputElement ? event.target : undefined);

/** 根据原生输入文本同步非法状态，兼容 Firefox 将非法字符转换为空字符串的行为。 */
const syncInputValidity = (event: Event) => {
  const input = getInput(event);
  if (!input) return;
  const insertedText = event instanceof InputEvent ? event.data : null;
  const insertedInvalidText = Boolean(insertedText && !isValidNumericText(insertedText));
  const inputText = input.value.trim();

  if (!inputText) {
    if (insertedInvalidText) hasInvalidInput.value = true;
    else if (event instanceof InputEvent && event.inputType.startsWith('delete')) hasInvalidInput.value = false;
    return;
  }

  hasInvalidInput.value = insertedInvalidText || !isValidNumericText(inputText);
};

const handleCompositionStart = () => {
  isComposing.value = true;
};

const handleCompositionUpdate = (event: CompositionEvent) => {
  if (event.data && !isValidNumericText(event.data)) hasInvalidInput.value = true;
};

const handleCompositionEnd = (event: CompositionEvent) => {
  isComposing.value = false;
  const hasInvalidComposition = Boolean(event.data && !isValidNumericText(event.data));
  syncInputValidity(event);
  hasInvalidInput.value = hasInvalidComposition || hasInvalidInput.value;
};

const handleModelUpdate = (value: NumberValue) => {
  if (isComposing.value || hasInvalidInput.value || (typeof value === 'number' && !Number.isFinite(value))) return;
  localValue.value = value;
  emit('update:modelValue', value);
};

const handleInput = (value: NumberValue) => {
  if (isComposing.value || hasInvalidInput.value || (typeof value === 'number' && !Number.isFinite(value))) return;
  emit('input', value);
};

const handleChange = (currentValue: NumberValue, previousValue: NumberValue) => {
  if (hasInvalidInput.value) return;
  emit('change', currentValue, previousValue);
};

const handleFocus = (event: FocusEvent) => {
  syncInputValidity(event);
  emit('focus', event);
};

/** 失焦时以输入框最终文本为准，非法字符串统一置空。 */
const handleBlur = (event: FocusEvent) => {
  const input = getInput(event);
  const shouldClear = hasInvalidInput.value || (input ? !isValidNumericText(input.value) : false);
  isComposing.value = false;
  hasInvalidInput.value = false;

  if (shouldClear) {
    const previousValue = localValue.value;
    localValue.value = null;
    if (input) input.value = '';
    emit('input', null);
    emit('update:modelValue', null);
    if (previousValue !== null) emit('change', null, previousValue);
  }

  emit('blur', event);
};

const focus = () => inputNumberRef.value?.focus();
const blur = () => inputNumberRef.value?.blur();

defineExpose({
  focus,
  blur,
});
</script>
